import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useDocs } from './hooks/useDocs';
import { DocsSidebar } from './components/DocsSidebar';
import { MarkdownText } from '@/components/ui/MarkdownText';
import { EmptyState } from '@/components/ui/EmptyState';
import { Drawer } from '@/components/ui/Drawer';

export function DocsViewerPage() {
  const { docId } = useParams<{ docId?: string }>();
  const { registry, content, isLoading, error } = useDocs(docId);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Fallback to the first doc if no docId is provided
  if (!docId && registry.length > 0) {
    return <Navigate to={`/docs/${registry[0].id}`} replace />;
  }

  const currentDoc = registry.find(doc => doc.id === docId);

  return (
    <div className="flex h-full bg-white">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-72 shrink-0">
        <DocsSidebar docs={registry} />
      </div>

      {/* Mobile Sidebar Drawer */}
      <Drawer
        isOpen={isMobileSidebarOpen}
        onClose={() => setIsMobileSidebarOpen(false)}
        title="Danh mục tài liệu"
        placement="left"
        size="sm"
      >
        <DocsSidebar docs={registry} onNavigate={() => setIsMobileSidebarOpen(false)} />
      </Drawer>

      {/* Main Content */}
      <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden relative">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center shrink-0 p-4 border-b border-slate-100 bg-white">
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen(true)}
            className="p-2 -ml-2 mr-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
          >
            <i className="ri-menu-2-line text-xl" />
          </button>
          <span className="text-sm font-semibold text-slate-800 line-clamp-1">
            {currentDoc ? currentDoc.title : 'Tài liệu hướng dẫn'}
          </span>
        </div>

        {/* Markdown Viewer */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-4 lg:p-8">
            {isLoading ? (
              <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-slate-200 rounded-md w-1/3"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-slate-100 rounded w-full"></div>
                  <div className="h-4 bg-slate-100 rounded w-5/6"></div>
                  <div className="h-4 bg-slate-100 rounded w-4/6"></div>
                </div>
                <div className="h-32 bg-slate-50 rounded-xl w-full mt-6"></div>
              </div>
            ) : error || !content ? (
              <div className="pt-10">
                <EmptyState
                  icon="ri-file-search-line"
                  title="Không tìm thấy bài viết"
                  description="Tài liệu bạn yêu cầu không tồn tại hoặc đã bị xóa."
                />
              </div>
            ) : (
              <MarkdownText content={content} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
